// Minimal, XSS-safe markdown renderer for UNTRUSTED LLM output.
//
// Security model: HTML-escape the ENTIRE input FIRST, then apply markdown
// transforms only over the already-escaped text. Because every `<`, `>`, `&`,
// `"` in the source is turned into an entity before any markup is produced, the
// LLM cannot inject raw HTML or <script>. The only tags in the output are the
// ones this file emits (<strong>, <em>, <code>, <pre>, <ul>, <ol>, <li>, <a>,
// <br>) and, for links, we hard-restrict the href scheme to http/https/mailto
// so `javascript:` URLs are neutralised.
//
// This supports: bold (**/__), italics (*/_), inline code (`), fenced code
// blocks (```), unordered/ordered lists, and links [text](url). It is
// deliberately small — enough to make agent turns legible, not a full CommonMark
// implementation.

function escapeHtml(text) {
  return String(text)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&#39;");
}

// Only allow safe link schemes; anything else (javascript:, data:, vbscript:…)
// is dropped so the text renders without a live link.
function safeHref(rawUrl) {
  const url = rawUrl.trim();
  if (/^(https?:|mailto:)/i.test(url)) {
    return url;
  }
  // Protocol-relative and root/relative links are safe too.
  if (/^(\/|#)/.test(url) || /^[\w./?=&%+-]+$/.test(url)) {
    return url;
  }
  return null;
}

// Inline transforms run over an already HTML-escaped fragment.
function renderInline(escaped) {
  // Pull inline code spans OUT behind placeholders before any other transform,
  // so their contents (which may contain *, _, [, ] — e.g. `assets/*`) can't be
  // mangled by the bold/italic/link rules below. Restored verbatim at the end.
  // The code text is already HTML-escaped, so this stays XSS-safe.   cannot
  // occur in escaped HTML, so it's a safe sentinel that no transform matches.
  const codeSpans = [];
  let out = escaped.replace(/`([^`]+)`/g, (_m, code) => {
    codeSpans.push(code);
    return `\ue000${codeSpans.length - 1}\ue000`;
  });

  // Links: [text](url). `text` is already escaped; validate the href scheme.
  out = out.replace(/\[([^\]]+)\]\(([^)\s]+)\)/g, (match, label, rawUrl) => {
    const href = safeHref(rawUrl);
    if (!href) {
      return label;
    }
    return `<a href="${escapeHtml(href)}" target="_blank" rel="noopener noreferrer nofollow">${label}</a>`;
  });

  // Bold before italics so ** is consumed before single *.
  out = out.replace(/\*\*([^*]+)\*\*/g, "<strong>$1</strong>");
  out = out.replace(/__([^_]+)__/g, "<strong>$1</strong>");
  out = out.replace(/\*([^*]+)\*/g, "<em>$1</em>");
  out = out.replace(/(^|[\s(])_([^_]+)_(?=[\s).,!?]|$)/g, "$1<em>$2</em>");

  // Restore the protected code spans (already-escaped content).
  out = out.replace(/\ue000(\d+)\ue000/g, (_m, n) => `<code>${codeSpans[Number(n)]}</code>`);

  return out;
}

// Split a GFM table row into trimmed cell strings, tolerating an optional
// leading/trailing pipe. Operates on already-escaped text; `|` is not an entity
// so it survives escaping and can be split on directly.
function splitTableCells(line) {
  let s = line.trim();
  if (s.startsWith("|")) {
    s = s.slice(1);
  }
  if (s.endsWith("|")) {
    s = s.slice(0, -1);
  }
  return s.split("|").map((cell) => cell.trim());
}

// A GFM separator row: every cell is dashes with optional leading/trailing colon
// for alignment (:---, :--:, ---:). Returns the per-column alignment array, or
// null when the line is not a separator (so a normal row never triggers a table).
function tableAlignments(line) {
  if (line == null || line.indexOf("-") === -1) {
    return null;
  }
  const cells = splitTableCells(line);
  if (cells.length === 0) {
    return null;
  }
  const aligns = [];
  for (const cell of cells) {
    if (!/^:?-+:?$/.test(cell)) {
      return null;
    }
    const left = cell.startsWith(":");
    const right = cell.endsWith(":");
    aligns.push(left && right ? "center" : right ? "right" : left ? "left" : null);
  }
  return aligns;
}

function alignAttr(align) {
  return align ? ` style="text-align:${align}"` : "";
}

// Build a <table> from an (already-escaped) header line, its alignment row, and
// the body lines. Cells run through renderInline so bold/code/links work inside.
function renderTable(headerLine, aligns, bodyLines) {
  const headers = splitTableCells(headerLine);
  const columns = headers.length;
  const th = headers
    .map((cell, i) => `<th${alignAttr(aligns[i])}>${renderInline(cell)}</th>`)
    .join("");
  const rows = bodyLines
    .map((line) => {
      const cells = splitTableCells(line);
      let tds = "";
      for (let i = 0; i < columns; i += 1) {
        tds += `<td${alignAttr(aligns[i])}>${renderInline(cells[i] ?? "")}</td>`;
      }
      return `<tr>${tds}</tr>`;
    })
    .join("");
  return `<table class="md-table"><thead><tr>${th}</tr></thead><tbody>${rows}</tbody></table>`;
}

// Render a block of already-escaped, non-code lines: tables, paragraphs + lists.
function renderBlock(lines) {
  const html = [];
  let list = null; // { type: "ul" | "ol", items: [] }

  const flushList = () => {
    if (!list) {
      return;
    }
    const inner = list.items.map((item) => `<li>${renderInline(item)}</li>`).join("");
    html.push(`<${list.type}>${inner}</${list.type}>`);
    list = null;
  };

  const paragraph = [];
  const flushParagraph = () => {
    if (paragraph.length === 0) {
      return;
    }
    // Soft-wrap: join the hard-wrapped source lines of one paragraph with a
    // single space FIRST, then run inline transforms over the whole thing —
    // standard markdown behavior (one <p>, not <br>). Joining before renderInline
    // matters: an inline span (**bold**, `code`, [link]) that is hard-wrapped
    // across two source lines only closes once the lines are joined; applying
    // renderInline per-line would leave a stray `**` because its pair sits on the
    // next line.
    html.push(`<p>${renderInline(paragraph.join(" "))}</p>`);
    paragraph.length = 0;
  };

  // A blockquote line starts with `>` — but escapeHtml has already turned `>`
  // into `&gt;`, so we match the ESCAPED form (`&gt;` optionally followed by a
  // space). Consecutive quote lines are one blockquote; the stripped inner text
  // is rendered through the SAME renderBlock so bold/links/code/paragraphs work
  // inside. A blank or non-quote line ends the quote (handled by the loop).
  const BLOCKQUOTE_RE = /^\s*&gt;\s?(.*)$/;
  const stripQuote = (line) => line.match(BLOCKQUOTE_RE)[1];

  for (let i = 0; i < lines.length; i += 1) {
    const line = lines[i];

    // Blockquote: gather all contiguous `&gt;`-prefixed lines, strip the marker,
    // and recurse so the inner text is full markdown (already-escaped, so still
    // XSS-safe — recursion never re-introduces raw tags).
    if (BLOCKQUOTE_RE.test(line)) {
      flushParagraph();
      flushList();
      const inner = [];
      let j = i;
      while (j < lines.length && BLOCKQUOTE_RE.test(lines[j])) {
        inner.push(stripQuote(lines[j]));
        j += 1;
      }
      html.push(`<blockquote>${renderBlock(inner)}</blockquote>`);
      i = j - 1;
      continue;
    }

    // ATX heading: `# ` .. `###### ` at line start -> <h1>..<h6>. `#` is not an
    // entity, so it survives escapeHtml and matches directly here; the heading
    // text still runs through renderInline (bold/code/links work inside).
    const headingMatch = line.match(/^(#{1,6})\s+(.+)$/);
    if (headingMatch) {
      flushParagraph();
      flushList();
      const level = headingMatch[1].length;
      html.push(`<h${level}>${renderInline(headingMatch[2].trim())}</h${level}>`);
      continue;
    }

    // GFM table: a header row containing a pipe, immediately followed by an
    // alignment/separator row. Consume the header, separator and all contiguous
    // body rows (a blank line or a pipe-less line ends the table).
    if (line.indexOf("|") !== -1) {
      const aligns = tableAlignments(lines[i + 1]);
      if (aligns) {
        flushParagraph();
        flushList();
        const body = [];
        let j = i + 2;
        while (j < lines.length && lines[j].trim() !== "" && lines[j].indexOf("|") !== -1) {
          body.push(lines[j]);
          j += 1;
        }
        html.push(renderTable(line, aligns, body));
        i = j - 1;
        continue;
      }
    }

    const ulMatch = line.match(/^\s*[-*+]\s+(.*)$/);
    const olMatch = line.match(/^\s*\d+\.\s+(.*)$/);

    if (ulMatch) {
      flushParagraph();
      if (!list || list.type !== "ul") {
        flushList();
        list = { type: "ul", items: [] };
      }
      list.items.push(ulMatch[1]);
    } else if (olMatch) {
      flushParagraph();
      if (!list || list.type !== "ol") {
        flushList();
        list = { type: "ol", items: [] };
      }
      list.items.push(olMatch[1]);
    } else if (line.trim() === "") {
      flushParagraph();
      flushList();
    } else if (list && paragraph.length === 0) {
      // A non-blank, non-marker line directly under a list item (no blank line
      // between) is a hard-wrapped CONTINUATION of that item — fold it into the
      // item, joined with a space, mirroring the paragraph soft-wrap. Without
      // this a wrapped bullet spills its tail out of the <ul> into a stray <p>.
      list.items[list.items.length - 1] += ` ${line.trim()}`;
    } else {
      flushList();
      paragraph.push(line);
    }
  }
  flushParagraph();
  flushList();
  return html.join("");
}

// Public entry point. Returns a SAFE HTML string built only from escaped input.
function renderMarkdown(source) {
  if (source == null || source === "") {
    return "";
  }

  const escaped = escapeHtml(source);
  const lines = escaped.split(/\r?\n/);

  const out = [];
  let block = [];
  let inFence = false;
  let fenceLines = [];

  for (const line of lines) {
    // ``` toggles a fenced code block. The fence marker itself may carry a
    // language hint which we ignore.
    if (/^\s*```/.test(line)) {
      if (inFence) {
        out.push(`<pre><code>${fenceLines.join("\n")}</code></pre>`);
        fenceLines = [];
        inFence = false;
      } else {
        out.push(renderBlock(block));
        block = [];
        inFence = true;
      }
      continue;
    }

    if (inFence) {
      fenceLines.push(line);
    } else {
      block.push(line);
    }
  }

  // An unterminated fence still renders as code (never as raw markup).
  if (inFence) {
    out.push(`<pre><code>${fenceLines.join("\n")}</code></pre>`);
  } else {
    out.push(renderBlock(block));
  }

  return out.join("");
}

export { renderMarkdown, escapeHtml };
